import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowRight, BookOpen, Hash, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Layout } from '@/components/layout/Layout';
import { ArticleCardFeatured, ArticleCard } from '@/components/article/ArticleCard';
import { PitchBackground, TacticalMotif } from '@/components/tactical/PitchBackground';
import { postsApi, categoriesApi, cmsApi } from '@/lib/api';

const HomePage = () => {
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [latestPosts, setLatestPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [homepageContent, setHomepageContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cms, cats] = await Promise.all([
        cmsApi.getHomepage(),
        categoriesApi.getPublicCategories(),
      ]);

      setHomepageContent(cms);
      setCategories(cats);

      // Yazı modu
      if (cms.articles_mode === 'featured') {
        const featured = await postsApi.getFeaturedPosts(3);
        setFeaturedPosts(featured);
      } else if (cms.articles_mode === 'manual' && cms.manual_slugs?.length) {
        const all = await postsApi.getPublicPosts({ limit: 100 });
        const bySlug = {};
        all.posts.forEach((p) => {
          bySlug[p.slug] = p;
        });
        const ordered = cms.manual_slugs
          .map((slug) => bySlug[slug])
          .filter(Boolean);
        setLatestPosts(ordered);
      } else {
        const latest = await postsApi.getLatestPosts(8);
        setLatestPosts(latest);
      }
    } catch (error) {
      console.error('Failed to fetch homepage data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{homepageContent?.custom_title || 'Taktik Analiz ve Oyun Modeli'} | TheAnalystRoom</title>
        <meta name="description" content={homepageContent?.custom_description || 'Futbolu positional play perspektifinden okumak. Taktik analiz, oyun modelleri, pozisyonlar ve modern futbolun stratejik derinliklerine yolculuk.'} />
      </Helmet>

      {/* Hero Section */}
      <PitchBackground variant="hero" className="relative bg-background">
        <section className="container-wide py-20 sm:py-28 lg:py-36">
          <div className="max-w-4xl">
            {/* Tactical Motif */}
            <div className="mb-8 opacity-60">
              <TacticalMotif variant="arrows" className="h-6" />
            </div>

            {/* Main Title */}
            <h1 className="heading-display text-foreground mb-6 text-balance">
              {homepageContent?.hero_title || 'TheAnalystRoom'}
            </h1>
            
            {/* Tagline */}
            <p className="text-xl sm:text-2xl text-muted-foreground font-serif italic mb-8 max-w-2xl">
              {homepageContent?.hero_tagline || 'Futbolu positional play perspektifinden okumak'}
            </p>
            
            {/* Description */}
            <p className="body-large text-muted-foreground max-w-xl mb-10">
              {homepageContent?.hero_description || 'Taktik analiz, oyun modelleri, pozisyonlar ve modern futbolun stratejik derinliklerine yolculuk. Her yazı, bir tez; her analiz, bir keşif.'}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Link to={homepageContent?.hero_primary_cta_url || "/yazilar"}>
                <Button 
                  size="lg" 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 px-6"
                >
                  <BookOpen className="mr-2 h-4 w-4" />
                  {homepageContent?.hero_primary_cta_label || 'Son Yazılar'}
                </Button>
              </Link>
              <Link to={homepageContent?.hero_secondary_cta_url || "/bulten"}>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-border text-foreground hover:bg-muted px-6"
                >
                  <Hash className="mr-2 h-4 w-4" />
                  {homepageContent?.hero_secondary_cta_label || 'Taktik Bülten'}
                </Button>
              </Link>
            </div>
          </div>

          {/* Decorative Half-Space Blocks */}
          <div className="hidden lg:block absolute right-12 top-1/2 -translate-y-1/2 opacity-[0.06]">
            <div className="flex gap-4">
              <div className="w-16 h-48 bg-foreground rounded-sm" />
              <div className="w-16 h-48 bg-foreground rounded-sm mt-12" />
            </div>
          </div>
        </section>

        {/* Horizontal Rule */}
        <div className="container-wide">
          <div className="h-px bg-border" />
        </div>
      </PitchBackground>

      {/* Editörün Seçtikleri (Featured Posts) */}
      {featuredPosts.length > 0 && (
        <section className="container-wide py-16 sm:py-20">
          <div className="flex items-center justify-between mb-10">
            <div>
              <span className="caption text-accent mb-2 block">Öne Çıkanlar</span>
              <h2 className="heading-section text-foreground">Editörün Seçtikleri</h2>
            </div>
            <Link 
              to="/yazilar" 
              className="hidden sm:flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-accent transition-colors"
            >
              Tüm yazılar
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredPosts.map((post) => (
              <ArticleCardFeatured key={post._id} post={post} />
            ))}
          </div>

          <Link 
            to="/yazilar" 
            className="sm:hidden flex items-center justify-center gap-1 mt-8 text-sm font-medium text-accent"
          >
            Tüm yazıları gör
            <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      )}

      {/* Horizontal Rule */}
      <div className="container-wide">
        <div className="h-px bg-border" />
      </div>

      {/* Son Yazılar (Latest Posts) */}
      <section className="container-wide py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Posts List */}
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="heading-section text-foreground">Son Yazılar</h2>
            </div>

            {latestPosts.length > 0 ? (
              <div className="space-y-6">
                {latestPosts.map((post) => (
                  <ArticleCard key={post._id} post={post} />
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-12">
                Henüz yayınlanmış yazı bulunmuyor.
              </p>
            )}

            {latestPosts.length > 0 && (
              <Link to="/yazilar">
                <Button 
                  variant="outline" 
                  className="mt-8 w-full sm:w-auto border-border"
                >
                  Tüm Yazıları Gör
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            {/* Categories */}
            <div className="mb-10">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-4">
                Kategoriler
              </h3>
              <div className="space-y-1">
                {categories.map((category) => (
                  <Link
                    key={category._id}
                    to={`/kategori/${category.slug}`}
                    className="flex items-center justify-between p-3 -mx-3 rounded-sm hover:bg-muted/50 transition-colors group"
                  >
                    <span className="text-sm font-medium text-foreground group-hover:text-accent transition-colors">
                      {category.name}
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors" />
                  </Link>
                ))}
              </div>
            </div>

            {/* Newsletter CTA */}
            <div className="p-6 bg-muted/50 border border-border rounded-sm">
              <h3 className="font-serif text-lg font-bold text-foreground mb-2">
                Bültene Katıl
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Yeni yazılardan ve analizlerden haberdar ol.
              </p>
              <Link to="/bulten">
                <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                  Abone Ol
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            {/* Tactical Decoration */}
            <div className="mt-8 flex justify-center opacity-20">
              <TacticalMotif variant="formation" className="h-16" />
            </div>
          </aside>
        </div>
      </section>

      {/* Horizontal Rule */}
      <div className="container-wide">
        <div className="h-px bg-border" />
      </div>

      {/* Category Tiles */}
      {categories.length > 0 && (
        <section className="container-wide py-16 sm:py-20">
          <div className="mb-10">
            <span className="caption text-accent mb-2 block">Keşfet</span>
            <h2 className="heading-section text-foreground">Kategoriler</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {categories.map((category) => (
              <Link
                key={category._id}
                to={`/kategori/${category.slug}`}
                className="group relative p-6 bg-card border border-border rounded-sm hover:border-accent/30 transition-all card-hover"
              >
                <h3 className="font-serif text-lg font-bold text-foreground mb-2 group-hover:text-accent transition-colors">
                  {category.name}
                </h3>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {category.description}
                </p>
                <ArrowRight className="absolute bottom-4 right-4 h-4 w-4 text-muted-foreground group-hover:text-accent group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Newsletter Banner */}
      <section className="bg-primary text-primary-foreground">
        <div className="container-wide py-16 sm:py-20">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold mb-4">
              Taktik Analizleri Kaçırma
            </h2>
            <p className="text-primary-foreground/70 mb-8">
              Haftalık bültenimizle yeni yazılardan, taktik güncellemelerinden ve özel içeriklerden haberdar ol.
            </p>
            <Link to="/bulten">
              <Button 
                variant="secondary" 
                size="lg"
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90"
              >
                Bültene Abone Ol
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
